import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { 
  Settings,
  User,
  Bell,
  BellOff,
  Camera,
  Trash2,
  Check,
  X,
  Loader2,
  AlertCircle,
  Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const SettingsPage = () => {
  const { user, refreshUser } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [displayName, setDisplayName] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  
  const [notificationsSupported, setNotificationsSupported] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [pushSubscription, setPushSubscription] = useState(null);
  
  const fileInputRef = useRef(null);
  const usernameTimeoutRef = useRef(null);

  useEffect(() => {
    fetchSettings();
    checkNotificationSupport();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
      setDisplayName(response.data.display_name || '');
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const checkNotificationSupport = async () => {
    if ('Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window) {
      setNotificationsSupported(true);
      setNotificationPermission(Notification.permission);
      
      // Check existing subscription
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setPushSubscription(subscription);
      } catch (error) {
        console.error('Failed to check push subscription:', error);
      }
    }
  };

  const checkUsernameAvailability = async (username) => {
    if (!username || username === settings?.display_name) {
      setUsernameAvailable(null);
      return;
    }
    
    setCheckingUsername(true);
    try {
      const response = await api.get(`/settings/check-username/${encodeURIComponent(username)}`);
      setUsernameAvailable(response.data.available);
    } catch (error) {
      setUsernameAvailable(null);
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleUsernameChange = (value) => {
    setDisplayName(value);
    setUsernameAvailable(null);
    
    // Debounce the availability check
    if (usernameTimeoutRef.current) {
      clearTimeout(usernameTimeoutRef.current);
    }
    
    usernameTimeoutRef.current = setTimeout(() => {
      checkUsernameAvailability(value);
    }, 500);
  };

  const handleSaveSettings = async () => {
    if (displayName !== settings?.display_name && usernameAvailable === false) {
      toast.error('Username is already taken');
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.put('/settings', {
        display_name: displayName
      });
      setSettings(response.data);
      if (refreshUser) refreshUser();
      toast.success('Settings saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File must be less than 5MB');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/settings/profile-picture', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSettings(prev => ({ ...prev, profile_picture: response.data.profile_picture }));
      if (refreshUser) refreshUser();
      toast.success('Profile picture updated!');
    } catch (error) {
      toast.error('Failed to upload profile picture');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteProfilePicture = async () => {
    try {
      await api.delete('/settings/profile-picture');
      setSettings(prev => ({ ...prev, profile_picture: null }));
      if (refreshUser) refreshUser();
      toast.success('Profile picture removed');
    } catch (error) {
      toast.error('Failed to remove profile picture');
    }
  };

  const requestNotificationPermission = async () => {
    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      
      if (permission === 'granted') {
        await subscribeToPush();
      }
    } catch (error) {
      toast.error('Failed to request notification permission');
    }
  };

  const subscribeToPush = async () => {
    try {
      // Get VAPID public key
      const keyResponse = await api.get('/push/vapid-public-key');
      const vapidPublicKey = keyResponse.data.publicKey;
      
      // Convert VAPID key to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };
      
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      
      // Send subscription to backend
      await api.post('/push/subscribe', {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
        }
      });
      
      setPushSubscription(subscription);
      setSettings(prev => ({ ...prev, notifications_enabled: true }));
      toast.success('Notifications enabled!');
    } catch (error) {
      console.error('Failed to subscribe:', error);
      toast.error('Failed to enable notifications');
    }
  };

  const unsubscribeFromPush = async () => {
    try {
      if (pushSubscription) {
        await api.post('/push/unsubscribe', {
          endpoint: pushSubscription.endpoint,
          keys: {}
        });
        await pushSubscription.unsubscribe();
      }
      
      setPushSubscription(null);
      setSettings(prev => ({ ...prev, notifications_enabled: false }));
      toast.success('Notifications disabled');
    } catch (error) {
      toast.error('Failed to disable notifications');
    }
  };

  const handleTestNotification = async () => {
    try {
      await api.post('/push/test');
      toast.success('Test notification sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test notification');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fadeIn" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold flex items-center gap-3">
          <Settings className="w-8 h-8" />
          Settings
        </h1>
        <p className="mt-1">Manage your account settings</p>
      </div>

      {/* Profile Picture Section */}
      <fieldset className="win-groupbox">
        <legend className="win-groupbox-label flex items-center gap-2">
          <Camera className="w-5 h-5" />
          Profile Picture
        </legend>

        <div className="flex items-center gap-6">
          <div className="relative">
            {settings?.profile_picture ? (
              <img
                src={settings.profile_picture}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-[var(--win-window-bg)] border-2 border-[var(--win-shadow)] flex items-center justify-center">
                <User className="w-10 h-10 text-[var(--win-shadow)]" />
              </div>
            )}

            {uploading && (
              <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleProfilePictureUpload}
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="gap-2"
              data-testid="upload-picture-btn"
            >
              <Camera className="w-4 h-4" />
              Upload Picture
            </Button>
            {settings?.profile_picture && (
              <Button
                onClick={handleDeleteProfilePicture}
                variant="outline"
                className="gap-2"
                data-testid="delete-picture-btn"
              >
                <Trash2 className="w-4 h-4" />
                Remove
              </Button>
            )}
            <p className="text-xs">Max 5MB. JPEG, PNG, WebP, or GIF</p>
          </div>
        </div>
      </fieldset>

      {/* Username Section */}
      <fieldset className="win-groupbox">
        <legend className="win-groupbox-label flex items-center gap-2">
          <User className="w-5 h-5" />
          Display Name
        </legend>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Username</Label>
            <div className="relative">
              <Input
                value={displayName}
                onChange={(e) => handleUsernameChange(e.target.value)}
                placeholder="Your display name"
                className="pr-10"
                data-testid="username-input"
              />
              {checkingUsername && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin" />
              )}
              {!checkingUsername && usernameAvailable === true && displayName !== settings?.display_name && (
                <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5" />
              )}
              {!checkingUsername && usernameAvailable === false && (
                <X className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5" />
              )}
            </div>
            {usernameAvailable === false && (
              <p className="text-xs flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                This username is already taken
              </p>
            )}
          </div>

          <div className="text-sm">
            <p><strong>Email:</strong> {settings?.email}</p>
            <p><strong>Member since:</strong> {settings?.created_at ? new Date(settings.created_at).toLocaleDateString() : 'N/A'}</p>
          </div>

          <Button
            onClick={handleSaveSettings}
            disabled={saving || (usernameAvailable === false) || displayName === settings?.display_name}
            className="gap-2"
            data-testid="save-settings-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </Button>
        </div>
      </fieldset>

      {/* Notifications Section */}
      <fieldset className="win-groupbox">
        <legend className="win-groupbox-label flex items-center gap-2">
          <Bell className="w-5 h-5" />
          Notifications
        </legend>

        {!notificationsSupported ? (
          <div className="win-well p-4">
            <p className="text-sm flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Push notifications are not supported in your browser
            </p>
          </div>
        ) : notificationPermission === 'denied' ? (
          <div className="win-well p-4">
            <p className="text-sm flex items-center gap-2">
              <BellOff className="w-5 h-5" />
              Notifications are blocked. Please enable them in your browser settings.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Push Notifications</p>
                <p className="text-sm">Get notified when someone comments on your cosplays</p>
              </div>
              <Switch
                checked={settings?.notifications_enabled && pushSubscription !== null}
                onCheckedChange={(checked) => {
                  if (checked) {
                    if (notificationPermission !== 'granted') {
                      requestNotificationPermission();
                    } else {
                      subscribeToPush();
                    }
                  } else {
                    unsubscribeFromPush();
                  }
                }}
                data-testid="notifications-toggle"
              />
            </div>
            
            {settings?.notifications_enabled && pushSubscription && (
              <Button
                onClick={handleTestNotification}
                variant="outline"
                className="gap-2"
                data-testid="test-notification-btn"
              >
                <Bell className="w-4 h-4" />
                Send Test Notification
              </Button>
            )}
          </div>
        )}
      </fieldset>
    </div>
  );
};

export default SettingsPage;
